import type { ReactNode } from "react";
import rawContent from "./marketplace-content.yaml";
import { MARKETPLACE_ICONS } from "./marketplace-icons";

/* ────── Types ────── */

export interface FeaturedGuide {
  name: string;
  slug: string;
  desc: string;
  icon: ReactNode;
  iconBg: string;
  buttonBg: string;
  rating: string;
  ratingNum: number;
  installs: string;
}

export interface CategoryGuide {
  name: string;
  slug: string;
  author: string;
  desc: string;
  rating: string;
  installs: string;
}

export interface Category {
  name: string;
  slug: string;
  count: number;
  icon: ReactNode;
  color: string;
  desc: string;
  guides: CategoryGuide[];
}

export interface Capability {
  title: string;
  desc: string;
  icon: ReactNode;
  color: string;
}

export interface Preview {
  title: string;
  subtitle?: string;
  value?: string;
  unit?: string;
  type: "metric" | "checklist" | "chart";
}

export interface Creator {
  name: string;
  desc: string;
  verified: boolean;
}

export interface Review {
  quote: string;
  detail: string;
}

/**
 * Full detail view for a marketplace guide.
 *
 * Featured guides are loaded with complete YAML detail blocks.
 * Category-only guides are synthesised with safe defaults:
 * empty `capabilities`, `previews`, and `reviews` arrays,
 * `ratingNum` parsed from the `rating` string (falls back to 0),
 * and a minimal `creator` derived from the guide author field.
 */
export interface GuideDetail {
  name: string;
  slug: string;
  installs: string;
  rating: string;
  ratingNum: number;
  buttonBg: string;
  subtitle: string;
  about: string[];
  capabilities: Capability[];
  previews: Preview[];
  creator: Creator;
  reviews: Review[];
}

/* ────── Helpers ────── */

/**
 * Convert a display name to a URL-safe slug.
 *
 * - NFD-normalizes then strips combining marks (diacritics)
 * - Lower-cases
 * - Replaces any run of non-alphanumeric characters with a single hyphen
 * - Trims leading / trailing hyphens
 */
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

function resolveIcon(key: string): ReactNode {
  const icon = MARKETPLACE_ICONS[key];
  if (!icon) {
    return null;
  }
  return icon;
}

/* ────── Raw YAML shape ────── */

interface RawCapability {
  title: string;
  desc: string;
  icon: string;
  color: string;
}

interface RawPreview {
  title: string;
  subtitle?: string;
  value?: string;
  unit?: string;
  type: "metric" | "checklist" | "chart";
}

interface RawDetail {
  subtitle: string;
  about: string[];
  capabilities: RawCapability[];
  previews: RawPreview[];
  creator: Creator;
  reviews: Review[];
}

interface RawFeatured {
  name: string;
  slug: string;
  desc: string;
  icon: string;
  iconBg: string;
  buttonBg: string;
  rating: string;
  ratingNum: number;
  installs: string;
  detail: RawDetail;
}

interface RawCategoryGuide {
  name: string;
  author: string;
  desc: string;
  rating: string;
  installs: string;
}

interface RawCategory {
  name: string;
  slug: string;
  count: number;
  icon: string;
  color: string;
  desc: string;
  guides: RawCategoryGuide[];
}

interface RawContent {
  featured: RawFeatured[];
  categories: RawCategory[];
}

/* ────── Resolved data ────── */

const content = rawContent as unknown as RawContent;

export const FEATURED: FeaturedGuide[] = content.featured.map((p) => ({
  name: p.name,
  slug: p.slug,
  desc: p.desc,
  icon: resolveIcon(p.icon),
  iconBg: p.iconBg,
  buttonBg: p.buttonBg,
  rating: p.rating,
  ratingNum: p.ratingNum,
  installs: p.installs,
}));

export const CATEGORIES: Category[] = content.categories.map((c) => ({
  name: c.name,
  slug: c.slug,
  count: c.count,
  icon: resolveIcon(c.icon),
  color: c.color,
  desc: c.desc,
  guides: c.guides.map((g) => ({ ...g, slug: slugify(g.name) })),
}));

export function getGuideBySlug(slug: string): GuideDetail | undefined {
  // Search featured guides first (they have full detail objects)
  const raw = content.featured.find((p) => p.slug === slug);
  if (raw) {
    const d = raw.detail;
    return {
      name: raw.name,
      slug: raw.slug,
      installs: raw.installs,
      rating: raw.rating,
      ratingNum: raw.ratingNum,
      buttonBg: raw.buttonBg,
      subtitle: d.subtitle,
      about: d.about,
      capabilities: d.capabilities.map((c) => ({
        title: c.title,
        desc: c.desc,
        icon: resolveIcon(c.icon),
        color: c.color,
      })),
      previews: d.previews,
      creator: d.creator,
      reviews: d.reviews,
    };
  }

  // Search category guides (minimal detail)
  for (const cat of content.categories) {
    const guide = cat.guides.find((g) => slugify(g.name) === slug);
    if (guide) {
      return {
        name: guide.name,
        slug,
        installs: guide.installs,
        rating: guide.rating,
        ratingNum: parseFloat(guide.rating) || 0,
        buttonBg: cat.color,
        subtitle: guide.desc,
        about: [guide.desc],
        capabilities: [],
        previews: [],
        creator: { name: guide.author, desc: "", verified: false },
        reviews: [],
      };
    }
  }

  return undefined;
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return CATEGORIES.find((c) => c.slug === slug);
}
